import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Stack,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Apple, CheckCircle, OpenInNew, PhoneIphone, Security } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { BRAND } from '../config/brand';
import { translatePublicSite } from '../i18n/publicSiteText';

const plans = [
  {
    name: 'Monthly Premium',
    price: '₹199',
    period: '/ month',
    detail: 'Flexible access to all Kundlistar Studio Premium features.',
  },
  {
    name: 'Yearly Premium',
    price: '₹999',
    period: '/ year',
    detail: 'The best-value way to use Kundlistar Studio for a full year.',
    badge: 'Best Value',
  },
];

const benefits = [
  'Nadi, KP, Vedic, BNN, Jaimini and Western chart modules',
  'Vedic Numerology, Lo Shu Grid, Panchang and Ruling Planets',
  'Client management, linked charts, planner, tasks and consultation records',
  'PDF report downloads for saved client charts',
  'Subscription management through your Apple ID',
];

const trackAppStoreClick = (placement: string) => {
  const browserWindow = window as Window & { dataLayer?: Array<Record<string, unknown>> };
  browserWindow.dataLayer = browserWindow.dataLayer || [];
  browserWindow.dataLayer.push({
    event: 'app_store_click',
    app: 'kundlistar_studio',
    placement,
  });
};

export default function InstallIPhone() {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const text = (english: string) => translatePublicSite(language, english);

  return (
    <Box sx={{ bgcolor: '#f6f8f6', minHeight: '100vh', py: { xs: 4, md: 8 }, textAlign: 'left' }}>
      <SEO
        title={text('Download Kundlistar Studio for iOS | iPhone & iPad')}
        description={text('Install Kundlistar Studio from the Apple App Store on iPhone or iPad. Choose ₹199 monthly or ₹999 yearly Premium inside the iOS app.')}
        path="/install-iphone"
        image="/promo-banner.png"
        keywords={text('Kundlistar Studio iPhone, astrology app iPhone, KP astrology iOS, Nadi astrology iPhone, ₹999 yearly astrology app')}
      />

      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #dce7df',
            borderRadius: { xs: 4, md: 6 },
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
              gap: { xs: 4, md: 7 },
              alignItems: 'center',
              p: { xs: 3, sm: 5, md: 7 },
              background: 'radial-gradient(circle at 92% 10%, rgba(198,154,55,0.2), transparent 28%), #fff',
            }}
          >
            <Box>
              <Chip
                icon={<Apple />}
                label={text('Available on the App Store')}
                sx={{ mb: 2.5, bgcolor: '#eef6f1', color: '#174e35', fontWeight: 800 }}
              />
              <Typography
                component="h1"
                sx={{
                  fontFamily: 'Georgia, Times New Roman, serif',
                  fontSize: { xs: '2.35rem', sm: '3.4rem' },
                  lineHeight: 1.05,
                  fontWeight: 900,
                  color: '#10251a',
                  letterSpacing: '-0.035em',
                }}
              >
                {text('Kundlistar Studio for iPhone and iPad')}
              </Typography>
              <Typography sx={{ mt: 2.5, color: '#52645a', fontSize: { xs: 16, sm: 19 }, lineHeight: 1.7, maxWidth: 650 }}>
                {text('Download the native iOS app for astrology charts, Numerology, client work and planning on iPhone and iPad. Premium is purchased securely inside the app.')}
              </Typography>

              <Button
                component="a"
                href={BRAND.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAppStoreClick('iphone_install_hero')}
                variant="contained"
                size="large"
                startIcon={<Apple />}
                endIcon={<OpenInNew />}
                sx={{
                  mt: 3.5,
                  bgcolor: '#10251a',
                  color: '#fff',
                  px: 3.5,
                  py: 1.4,
                  borderRadius: 3,
                  fontWeight: 900,
                  '&:hover': { bgcolor: '#1c4431' },
                }}
              >
                {text('Download on the App Store')}
              </Button>
            </Box>

            <Box sx={{ display: 'grid', placeItems: 'center' }}>
              <Box
                sx={{
                  width: { xs: 230, sm: 270 },
                  minHeight: { xs: 430, sm: 500 },
                  border: '9px solid #10251a',
                  borderRadius: 8,
                  bgcolor: '#f7faf8',
                  boxShadow: '0 28px 65px rgba(16,37,26,0.24)',
                  p: 2.25,
                }}
              >
                <Box sx={{ width: 70, height: 7, borderRadius: 99, bgcolor: '#10251a', mx: 'auto', mb: 3 }} />
                <Stack direction="row" alignItems="center" spacing={1.2}>
                  <Box component="img" src="/logo.png" alt="" sx={{ width: 42, height: 42, borderRadius: 2, objectFit: 'contain' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: '#143d28' }}>Kundlistar Studio</Typography>
                    <Typography sx={{ fontSize: 10, color: '#738077' }}>{text('Professional workspace')}</Typography>
                  </Box>
                </Stack>
                <Stack spacing={1.2} sx={{ mt: 3 }}>
                  {['Astrology Systems', 'Numerology', 'Clients', 'Planner'].map((item) => (
                    <Paper key={item} elevation={0} sx={{ p: 1.5, border: '1px solid #dce7df', borderRadius: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircle sx={{ color: '#2d6a4f', fontSize: 19 }} />
                        <Typography sx={{ color: '#143d28', fontWeight: 800, fontSize: 13 }}>{text(item)}</Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                <Button fullWidth variant="contained" startIcon={<PhoneIphone />} sx={{ mt: 3, bgcolor: '#2d6a4f', fontWeight: 800 }}>
                  {text('Open Studio')}
                </Button>
              </Box>
            </Box>
          </Box>

          <Box id="pricing" sx={{ bgcolor: '#f8faf8', px: { xs: 3, sm: 5, md: 7 }, py: { xs: 5, md: 7 }, borderTop: '1px solid #e2e9e4' }}>
            <Typography variant="overline" sx={{ color: '#9b7421', fontWeight: 900, letterSpacing: '0.14em' }}>
              {text('IOS PREMIUM')}
            </Typography>
            <Typography variant="h3" sx={{ mt: 0.75, color: '#10251a', fontWeight: 900 }}>
              {text('Choose monthly or yearly')}
            </Typography>
            <Typography sx={{ mt: 1.5, color: '#607066', maxWidth: 720 }}>
              {text('Both plans unlock the same Premium workspace. Complete the purchase inside the Kundlistar Studio iOS app on iPhone or iPad using your Apple ID.')}
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              {plans.map((plan) => (
                <Grid key={plan.name} size={{ xs: 12, md: 6 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      borderWidth: plan.badge ? 2 : 1,
                      borderColor: plan.badge ? '#2d6a4f' : '#dce7df',
                      position: 'relative',
                    }}
                  >
                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                      {plan.badge && <Chip label={text(plan.badge)} color="primary" size="small" sx={{ mb: 2, fontWeight: 800 }} />}
                      <Typography sx={{ fontWeight: 900, color: '#143d28', fontSize: 20 }}>{text(plan.name)}</Typography>
                      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 1.5 }}>
                        <Typography sx={{ color: '#143d28', fontWeight: 950, fontSize: 44, lineHeight: 1 }}>{plan.price}</Typography>
                        <Typography sx={{ color: '#65736b', fontWeight: 700 }}>{text(plan.period)}</Typography>
                      </Stack>
                      <Typography sx={{ color: '#607066', lineHeight: 1.7 }}>{text(plan.detail)}</Typography>
                      <Button
                        component="a"
                        href={BRAND.appStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackAppStoreClick(`iphone_pricing_${plan.name.toLowerCase().replace(/\s+/g, '_')}`)}
                        fullWidth
                        variant={plan.badge ? 'contained' : 'outlined'}
                        startIcon={<Apple />}
                        sx={{ mt: 3, py: 1.25, fontWeight: 900 }}
                      >
                        {text('Get this plan in the iOS app')}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ px: { xs: 3, sm: 5, md: 7 }, py: { xs: 5, md: 7 } }}>
            <Grid container spacing={2.5}>
              {benefits.map((benefit) => (
                <Grid key={benefit} size={{ xs: 12, sm: 6 }}>
                  <Stack direction="row" spacing={1.3} alignItems="flex-start">
                    <CheckCircle sx={{ color: '#2d6a4f', mt: 0.25 }} />
                    <Typography sx={{ color: '#34483c', lineHeight: 1.65 }}>{text(benefit)}</Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 4, color: '#65736b' }}>
              <Security fontSize="small" />
              <Typography variant="body2">
                {text('Payment, renewal and cancellation are handled through Apple. App Store terms apply.')}
              </Typography>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
